import { useEffect, useRef, useCallback } from 'react';

/**
 * Configuration Mercure
 */
const MERCURE_HUB_URL = process.env.REACT_APP_MERCURE_URL || 'http://localhost:3000/.well-known/mercure';

/**
 * Topics disponibles pour les abonnements
 */
export const MercureTopics = {
  INTERVENTIONS: 'interventions',
  INTERVENTION: (id: number) => `intervention/${id}`,
  EQUIPES: 'equipes',
  EQUIPE: (id: number) => `equipe/${id}`,
  REGULATION: 'regulation',
  CONFLITS: 'conflits',
  // Lot VH-3 : vehicules planifies et leur equipage
  ENGINS_PLANIFIES: 'engins-planifies',
  ENGIN_PLANIFIE: (id: number) => `engin-planifie/${id}`,
} as const;

/**
 * Types d'evenements Mercure
 */
export interface MercureEvent<T = unknown> {
  type: string;
  data?: T;
  timestamp: string;
}

export interface InterventionEvent extends MercureEvent {
  type: 'created' | 'updated' | 'statut_change';
  intervention?: {
    id: number;
    numero_ordre: string;
    statut: string;
    statut_label: string;
    priorite: string;
    parking: string;
    date_intervention: string;
    heure_souhaitee: string;
  };
  intervention_id?: number;
  numero_ordre?: string;
  ancien_statut?: string;
  nouveau_statut?: string;
  parking?: string;
}

export interface AffectationEvent extends MercureEvent {
  type: 'affectation_created' | 'affectation_updated';
  affectation: {
    id: number;
    intervention_id: number;
    intervention_numero: string;
    equipe_id: number;
    equipe_nom: string;
    zone: number;
    statut: string;
  };
}

export interface ConflitEvent extends MercureEvent {
  type: 'conflit_detected' | 'retard_detected';
  conflit?: {
    type: string;
    niveau: string;
    message: string;
  };
  intervention?: {
    id: number;
    numero_ordre: string;
    parking: string;
  };
  ecart_minutes?: number;
}

export interface RegulationEvent extends MercureEvent {
  type:
    | 'intervention_created'
    | 'intervention_updated'
    | 'statut_change'
    | 'affectation_created'
    | 'affectation_updated'
    | 'charge_update'
    | 'conflit_detected'
    | 'retard_detected'
    | 'plan_applied'
    | 'timeline_refresh'
    // SFD jalonnement Contact/Large (Lot 5 - #125) : MercureService::
    // publishInterventionUpdate() publie 'intervention_' + eventType sur ce
    // topic pour chaque action jalon/IMAT/configuration cote agent.
    | 'intervention_jalon_debut'
    | 'intervention_jalon_fin'
    | 'intervention_jalon_corrige'
    | 'intervention_jalon_incident'
    | 'intervention_configuration_maj'
    | 'intervention_immatriculation_maj';
}

/**
 * Options du hook useMercure
 */
interface UseMercureOptions {
  /** Topics auxquels s'abonner */
  topics: string[];
  /** Callback appele lors de la reception d'un evenement */
  onMessage: (event: MercureEvent) => void;
  /** Callback appele en cas d'erreur */
  onError?: (error: Event) => void;
  /** Activer/desactiver l'abonnement */
  enabled?: boolean;
}

/**
 * Hook pour s'abonner aux mises a jour Mercure en temps reel
 *
 * @example
 * ```tsx
 * useMercure({
 *   topics: [MercureTopics.REGULATION],
 *   onMessage: (event) => {
 *     if (event.type === 'statut_change') {
 *       // Rafraichir les donnees
 *       queryClient.invalidateQueries(['timeline']);
 *     }
 *   },
 * });
 * ```
 */
export function useMercure({ topics, onMessage, onError, enabled = true }: UseMercureOptions): void {
  const eventSourceRef = useRef<EventSource | null>(null);
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  // Mettre a jour les refs pour eviter les re-renders
  useEffect(() => {
    onMessageRef.current = onMessage;
    onErrorRef.current = onError;
  }, [onMessage, onError]);

  useEffect(() => {
    if (!enabled || topics.length === 0) {
      return;
    }

    // Construire l'URL avec les topics
    const url = new URL(MERCURE_HUB_URL);
    topics.forEach((topic) => {
      url.searchParams.append('topic', topic);
    });

    // Creer la connexion EventSource
    const eventSource = new EventSource(url.toString(), {
      withCredentials: false,
    });

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as MercureEvent;
        onMessageRef.current(data);
      } catch (error) {
        console.error('Erreur parsing Mercure event:', error);
      }
    };

    eventSource.onerror = (event) => {
      console.error('Erreur Mercure:', event);
      onErrorRef.current?.(event);
    };

    eventSourceRef.current = eventSource;

    // Cleanup
    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [topics.join(','), enabled]);
}

/**
 * Hook simplifie pour le module Regulation
 * S'abonne aux evenements de regulation et invalide les queries React Query
 */
export function useRegulationMercure(
  onEvent: (event: RegulationEvent) => void,
  enabled = true
): void {
  const handleMessage = useCallback(
    (event: MercureEvent) => {
      onEvent(event as RegulationEvent);
    },
    [onEvent]
  );

  useMercure({
    topics: [MercureTopics.REGULATION, MercureTopics.CONFLITS],
    onMessage: handleMessage,
    enabled,
  });
}

/**
 * Hook pour s'abonner aux mises a jour d'une intervention specifique
 */
export function useInterventionMercure(
  interventionId: number | undefined,
  onEvent: (event: InterventionEvent) => void,
  enabled = true
): void {
  const handleMessage = useCallback(
    (event: MercureEvent) => {
      onEvent(event as InterventionEvent);
    },
    [onEvent]
  );

  useMercure({
    topics: interventionId
      ? [MercureTopics.INTERVENTION(interventionId)]
      : [],
    onMessage: handleMessage,
    enabled: enabled && !!interventionId,
  });
}

export default useMercure;
